import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaList,
  FaChartPie,
  FaHome,
  FaTh,
  FaBox,
  FaTags,
  FaSignOutAlt,
  FaChevronRight,
  FaSearch,
  FaBars,
} from "react-icons/fa";
import { AuthContext } from "../components/AuthContext";
import { API_URL } from "../constant";

const Dashboard = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const [totalCategories, setTotalCategories] = useState(0);
  const [totalSubcategories, setTotalSubcategories] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [allProducts, setAllProducts] = useState([]); // Store all products
  const [searchQuery, setSearchQuery] = useState(""); // State for search query

  // State for toggling sidebar on mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return allProducts.slice(0, 6); // Return first 6 products if no search

    return allProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allProducts, searchQuery]);

  useEffect(() => {
    // If the user is not authenticated, redirect to login
    if (!authToken) {
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        };

        // Fetch Categories count
        const categoriesResponse = await fetch(
          `${API_URL}/api/category/count`,
          { headers }
        );
        const categoriesData = await categoriesResponse.json();
        setTotalCategories(categoriesData.total_categories || 0);

        // Fetch Subcategories count
        const subcategoriesResponse = await fetch(
          `${API_URL}/api/subcategory/count`,
          { headers }
        );
        const subcategoriesData = await subcategoriesResponse.json();
        setTotalSubcategories(subcategoriesData.total_subcategories || 0);

        // Fetch Products count
        const productsResponse = await fetch(`${API_URL}/api/products/count/total`, {
          headers,
        });
        const productsData = await productsResponse.json();
        setTotalProducts(productsData.total_products || 0);

        // Fetch products list
        const productsListResponse = await fetch(`${API_URL}/api/products`, {
          headers,
        });
        const productsListData = await productsListResponse.json();
        // Sort products by id descending (assuming higher id means later added)
        const sortedProducts = productsListData.products.sort(
          (a, b) => b.id - a.id
        );
        setAllProducts(sortedProducts); // Store all products
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [authToken, navigate]);

  const handleStatusChange = async (productId, newStatus) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };

      // Use the dedicated status endpoint
      await fetch(`${API_URL}/api/products/${productId}/status`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ status: newStatus }),
      });

      // Update local state
      setAllProducts(
        allProducts.map((product) =>
          product.id === productId ? { ...product, status: newStatus } : product
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white shadow-md">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          <FaBars className="text-2xl text-gray-600" />
        </button>
        <span className="text-xl font-bold text-gray-700">Dashboard</span>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? "block" : "hidden"
          } md:block w-full md:w-64 bg-white shadow-md flex flex-col`}
        >
          <div className="hidden md:flex items-center justify-center py-6">
            <div className="text-3xl font-bold text-gray-600 flex items-center">
              <FaChartPie style={{ fontSize: "48px", marginRight: "10px" }} />
              <span
                className="text-2xl md:text-3xl font-bold"
                style={{ color: "gray-600" }}
              >
                Dashboard
              </span>
            </div>
          </div>
          {/* Top Section */}
          <nav className="mt-6 flex flex-col flex-grow text-gray-600">
            <ul>
              {[
                {
                  name: "Home",
                  icon: (
                    <FaHome className="mr-2" style={{ fontSize: "20px" }} />
                  ),
                  link: "/Dashboard",
                },
                {
                  name: "Categories",
                  icon: (
                    <FaList className="mr-2" style={{ fontSize: "20px" }} />
                  ),
                  link: "/Categories",
                },
                {
                  name: "Subcategories",
                  icon: <FaTh className="mr-2" style={{ fontSize: "20px" }} />,
                  link: "/Subcategories",
                },
                {
                  name: "Products",
                  icon: <FaBox className="mr-2" style={{ fontSize: "20px" }} />,
                  link: "/Products",
                },
                {
                  name: "Attributes",
                  icon: (
                    <FaTags className="mr-2" style={{ fontSize: "20px" }} />
                  ),
                  link: "/Attributes",
                },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate(item.link)}
                  className={`cursor-pointer px-6 py-2 rounded-lg flex items-center justify-between text-lg ${
                    item.name === "Home"
                      ? "bg-gray-200 scale-90"
                      : "hover:bg-gray-200 hover:scale-90"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <FaChevronRight className="text-gray-600" />
                </li>
              ))}
            </ul>
          </nav>
          {/* Bottom Section */}
          <div className="mt-auto flex flex-col text-red-600">
            <ul>
              {[
                {
                  name: "Signout",
                  icon: (
                    <FaSignOutAlt
                      className="mr-2"
                      style={{ fontSize: "20px" }}
                    />
                  ),
                },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate("/Logout")}
                  className="cursor-pointer px-6 py-2 hover:bg-gray-200 rounded-lg flex items-center justify-between text-lg"
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <header className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex-1 flex justify-center mb-4 md:mb-0">
              <div className="relative w-full max-w-3xl">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600"
                  aria-label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </header>

          {/* Overview Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {/* Total Categories */}
            <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
              <div className="bg-gray-200 p-3 rounded-full">
                <FaList className="text-gray-600 text-3xl" />
              </div>
              <div className="ml-4">
                <div className="text-gray-600 font-medium text-lg">
                  Total Categories
                </div>
                <div className="text-2xl font-bold text-gray-600">
                  {totalCategories}
                </div>
              </div>
            </div>
            {/* Total Subcategories */}
            <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
              <div className="bg-gray-200 p-3 rounded-full">
                <FaTh className="text-gray-600 text-3xl" />
              </div>
              <div className="ml-4">
                <div className="text-gray-600 font-medium text-lg">
                  Total Subcategories
                </div>
                <div className="text-2xl font-bold text-gray-600">
                  {totalSubcategories}
                </div>
              </div>
            </div>
            {/* Total Products */}
            <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
              <div className="bg-gray-200 p-3 rounded-full">
                <FaBox className="text-gray-600 text-3xl" />
              </div>
              <div className="ml-4">
                <div className="text-gray-600 font-medium text-lg">
                  Total Products
                </div>
                <div className="text-2xl font-bold text-gray-600">
                  {totalProducts}
                </div>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md overflow-x-auto">
            <div className="text-xl text-gray-700 font-bold mb-4">
              {searchQuery ? "Search Results" : "Recent Products"}
            </div>
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-200">
                  {[
                    "Product Name",
                    "Category",
                    "Price",
                    "Quantity",
                    "Status",
                  ].map((head, idx) => (
                    <th key={idx} className="text-left text-gray-700 py-2 px-4">
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts && filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <tr key={product.id} className="border-t text-gray-700">
                      <td className="py-2 px-4">{product.name}</td>
                      <td className="py-2 px-4">{product.category}</td>
                      <td className="py-2 px-4">{product.price}</td>
                      <td className="py-2 px-4">{product.stock_quantity}</td>
                      <td className="py-2 px-4">
                        <select
                          className="border p-1 rounded-lg"
                          value={product.status}
                          onChange={(e) =>
                            handleStatusChange(product.id, e.target.value)
                          }
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-600">
                      {searchQuery
                        ? "No products match your search."
                        : "No products found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;
